import React, { useState, useEffect } from 'react';
import { accountingService, featureFlagsService } from '../services/api';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  DollarSign, 
  FileText, 
  CheckCircle,
  Clock,
  AlertTriangle,
  RefreshCw,
  Download,
  CreditCard
} from 'lucide-react';

const AccountingDashboard = () => {
  const [featureEnabled, setFeatureEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [settlements, setSettlements] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedSettlement, setSelectedSettlement] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('direct_deposit');
  const [paymentRef, setPaymentRef] = useState('');

  useEffect(() => {
    checkFeatureAndLoad();
  }, []);

  const checkFeatureAndLoad = async () => {
    try {
      const enabledFeatures = await featureFlagsService.getEnabledFeatures();
      const isEnabled = enabledFeatures.accounting_module_enabled === true;
      setFeatureEnabled(isEnabled);
      
      if (isEnabled) {
        await loadData();
      }
    } catch (err) {
      setFeatureEnabled(false);
    } finally {
      setLoading(false);
    }
  };

  const loadData = async () => {
    try {
      const [dashboardData, settlementsData] = await Promise.all([
        accountingService.getDashboard().catch(() => null),
        accountingService.getSettlements().catch(() => ({ settlements: [] }))
      ]);
      
      setDashboard(dashboardData);
      setSettlements(settlementsData.settlements || []);
    } catch (err) {
      console.error('Failed to load accounting data:', err);
    }
  };

  const handleFinalize = async (settlementId) => {
    if (!window.confirm('Finalize this settlement? This action indicates the settlement is ready for payment.')) return;
    try {
      await accountingService.finalizeSettlement(settlementId, 'Finalized for payment');
      alert('Settlement finalized successfully!');
      loadData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to finalize settlement');
    }
  };

  const handleMarkPaid = async () => {
    if (!selectedSettlement) return;
    try {
      await accountingService.markSettlementPaid(
        selectedSettlement.id,
        paymentMethod,
        paymentRef || null
      );
      alert('Settlement marked as paid!');
      setShowPaymentModal(false);
      setSelectedSettlement(null);
      setPaymentMethod('direct_deposit');
      setPaymentRef('');
      loadData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to mark as paid');
    }
  };

  const handleExport = async () => {
    try {
      const blob = await accountingService.exportSettlements();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `settlements_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
    } catch (err) {
      alert('Failed to export settlements');
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      draft: { color: 'bg-gray-100 text-gray-800', icon: FileText },
      finalized: { color: 'bg-blue-100 text-blue-800', icon: Clock },
      paid: { color: 'bg-green-100 text-green-800', icon: CheckCircle }
    };
    const { color, icon: Icon } = config[status] || config.draft;
    return (
      <Badge className={`${color} flex items-center gap-1`}>
        <Icon className="w-3 h-3" />
        {status?.toUpperCase()}
      </Badge>
    );
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-96">
          <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </DashboardLayout>
    );
  }

  if (!featureEnabled) {
    return (
      <DashboardLayout>
        <div className="max-w-2xl mx-auto p-6">
          <Card className="border-yellow-200 bg-yellow-50">
            <CardContent className="pt-6">
              <div className="flex items-start gap-4">
                <AlertTriangle className="w-8 h-8 text-yellow-600 flex-shrink-0" />
                <div>
                  <h2 className="text-xl font-semibold text-yellow-800">Feature Not Enabled</h2>
                  <p className="text-yellow-700 mt-2">
                    The Accounting Module is currently disabled. Contact your administrator to enable this feature.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto p-4 md:p-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Accounting Dashboard</h1>
            <p className="text-gray-600 mt-1">Settlement management and payment tracking</p>
          </div>
          <Button onClick={handleExport} variant="outline" className="flex items-center gap-2">
            <Download className="w-4 h-4" /> Export CSV
          </Button>
        </div>

        {/* Stats */}
        {dashboard && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center gap-3">
                  <FileText className="w-8 h-8 text-gray-600" />
                  <div>
                    <div className="text-2xl font-bold">{dashboard.stats?.draft || 0}</div>
                    <div className="text-sm text-gray-500">Draft</div>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center gap-3">
                  <Clock className="w-8 h-8 text-blue-600" />
                  <div>
                    <div className="text-2xl font-bold">{dashboard.stats?.finalized || 0}</div>
                    <div className="text-sm text-gray-500">Pending Payment</div>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                  <div>
                    <div className="text-2xl font-bold">{dashboard.stats?.paid || 0}</div>
                    <div className="text-sm text-gray-500">Paid</div>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center gap-3">
                  <DollarSign className="w-8 h-8 text-orange-600" />
                  <div>
                    <div className="text-2xl font-bold">${(dashboard.stats?.total_pending_amount || 0).toLocaleString()}</div>
                    <div className="text-sm text-gray-500">Pending Amount</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tabs */}
        <div className="flex border-b mb-6">
          {['overview', 'settlements', 'pending'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 font-medium capitalize ${
                activeTab === tab 
                  ? 'text-blue-600 border-b-2 border-blue-600' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab === 'pending' ? 'Pending Payments' : tab}
            </button>
          ))}
        </div>

        {activeTab === 'overview' && (
          <Card>
            <CardHeader>
              <CardTitle>Recent Settlements</CardTitle>
            </CardHeader>
            <CardContent>
              {(dashboard?.recent_settlements || []).length === 0 ? (
                <div className="text-center py-8 text-gray-500">No recent settlements</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-3 px-2">Driver</th>
                        <th className="text-left py-3 px-2">Period</th>
                        <th className="text-left py-3 px-2">Gross</th>
                        <th className="text-left py-3 px-2">Net</th>
                        <th className="text-left py-3 px-2">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(dashboard?.recent_settlements || []).map(s => (
                        <tr key={s.id} className="border-b hover:bg-gray-50">
                          <td className="py-3 px-2">{s.driver_id?.slice(0, 8)}...</td>
                          <td className="py-3 px-2 text-xs">{s.period_start} - {s.period_end}</td>
                          <td className="py-3 px-2">${s.gross_pay?.toLocaleString()}</td>
                          <td className="py-3 px-2 font-medium">${s.net_pay?.toLocaleString()}</td>
                          <td className="py-3 px-2">{getStatusBadge(s.payment_status || s.status)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {activeTab === 'settlements' && (
          <Card>
            <CardHeader>
              <CardTitle>All Settlements</CardTitle>
            </CardHeader>
            <CardContent>
              {settlements.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <DollarSign className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No settlements found.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-3 px-2">ID</th>
                        <th className="text-left py-3 px-2">Driver</th>
                        <th className="text-left py-3 px-2">Period</th>
                        <th className="text-left py-3 px-2">Gross</th>
                        <th className="text-left py-3 px-2">Deductions</th>
                        <th className="text-left py-3 px-2">Net</th>
                        <th className="text-left py-3 px-2">Status</th>
                        <th className="text-left py-3 px-2">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {settlements.map(s => (
                        <tr key={s.id} className="border-b hover:bg-gray-50">
                          <td className="py-3 px-2 text-xs font-mono">{s.id?.slice(0, 8)}...</td>
                          <td className="py-3 px-2">{s.driver_id?.slice(0, 8)}...</td>
                          <td className="py-3 px-2 text-xs">{s.period_start}<br/>{s.period_end}</td>
                          <td className="py-3 px-2">${s.gross_pay?.toLocaleString()}</td>
                          <td className="py-3 px-2 text-red-600">-${s.total_deductions?.toLocaleString()}</td>
                          <td className="py-3 px-2 font-medium text-green-600">${s.net_pay?.toLocaleString()}</td>
                          <td className="py-3 px-2">{getStatusBadge(s.payment_status || s.status)}</td>
                          <td className="py-3 px-2">
                            <div className="flex gap-1">
                              {(s.payment_status === 'draft' || !s.payment_status) && (
                                <Button size="sm" variant="outline" onClick={() => handleFinalize(s.id)}>
                                  Finalize
                                </Button>
                              )}
                              {s.payment_status === 'finalized' && (
                                <Button size="sm" onClick={() => {
                                  setSelectedSettlement(s);
                                  setShowPaymentModal(true);
                                }}>
                                  <CreditCard className="w-3 h-3 mr-1" /> Pay
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {activeTab === 'pending' && (
          <Card>
            <CardHeader>
              <CardTitle>Pending Payments</CardTitle>
            </CardHeader>
            <CardContent>
              {settlements.filter(s => s.payment_status === 'finalized').length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <CheckCircle className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No pending payments.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {settlements.filter(s => s.payment_status === 'finalized').map(s => (
                    <div key={s.id} className="border rounded-lg p-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="font-medium">Settlement #{s.id?.slice(0, 8)}</div>
                          <div className="text-sm text-gray-500">{s.period_start} - {s.period_end}</div>
                        </div>
                        <div className="text-right">
                          <div className="text-xl font-bold text-green-600">${s.net_pay?.toLocaleString()}</div>
                          {getStatusBadge('finalized')}
                        </div>
                      </div>
                      <div className="mt-4">
                        <Button onClick={() => {
                          setSelectedSettlement(s);
                          setShowPaymentModal(true);
                        }}>
                          <CreditCard className="w-4 h-4 mr-2" /> Mark as Paid
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Payment Modal */}
        {showPaymentModal && selectedSettlement && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-md w-full p-6">
              <h3 className="text-lg font-semibold mb-4">Record Payment</h3>
              <div className="mb-4 p-3 bg-gray-50 rounded">
                <div className="text-sm text-gray-500">Amount to Pay</div>
                <div className="text-2xl font-bold text-green-600">${selectedSettlement.net_pay?.toLocaleString()}</div>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Payment Method *</label>
                  <select
                    value={paymentMethod}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                    className="w-full border rounded-md p-2"
                  >
                    <option value="direct_deposit">Direct Deposit</option>
                    <option value="check">Check</option>
                    <option value="wire_transfer">Wire Transfer</option>
                    <option value="comcheck">Comcheck</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Reference Number</label>
                  <input
                    type="text"
                    value={paymentRef}
                    onChange={(e) => setPaymentRef(e.target.value)}
                    className="w-full border rounded-md p-2"
                    placeholder="Check #, Transaction ID, etc."
                  />
                </div>
              </div>
              <div className="flex gap-3 justify-end mt-6">
                <Button variant="outline" onClick={() => {
                  setShowPaymentModal(false);
                  setSelectedSettlement(null);
                }}>Cancel</Button>
                <Button onClick={handleMarkPaid}>Confirm Payment</Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default AccountingDashboard;
